import { defineStore } from "pinia";
import { instance } from "..";
import { ELoader, useAdaptLoaderStore } from "./adapt-loader";
import { QuizRest } from "@/services/rest/quiz.rest";
import { lastValueFrom, take } from "rxjs";
import { QuizData, StudentQuiz } from "@/models/quiz.model";
import { useLearningObjectiveStore } from "./learning-objective";
import { useAdaptCourseStore } from "./adapt-course";
import { getUserFromStorage } from "@/utils/user-store.util";
import { TypeAlert, useAlertStore } from "./alert";
import { StatusProgress, useAdaptProgressStore } from "./adapt-progress";
import { RouteMaterials, useAdaptMaterialStore } from "./adapt-material";
import { EFeedback, useFeedbackStore } from "./feedback";
import { CourseStore } from "../course";
import { EventsRest } from "@/services/rest/events.rest";

type ProceedQuizType =
  | "quiz-materials"
  | "objective"
  | "proceed"
  | "more-materials";

type ProceedQuizStrategy = () => void;

const strategyMap: Record<ProceedQuizType, ProceedQuizStrategy> = {
  "quiz-materials": () => {
    useAdaptMaterialStore?.setMoreMaterial(RouteMaterials.QUIZ);
  },
  objective: () => {},
  proceed: () => {
    useFeedbackStore?.set(EFeedback.OBJECTIVE);
    useAdaptProgressStore?.setNextState();
  },
  "more-materials": () => {
    useAdaptMaterialStore?.setMoreMaterial(RouteMaterials.MORE);
  },
};

export const store = defineStore({
  id: "adapt-quiz",
  state: () => ({
    quizData: {} as QuizData,
    quizIntro: {} as StudentQuiz,
    quizHasEnded: false as boolean,
    isOpened: false,
    messageData: {} as any,
    percentage: 0,
  }),
  getters: {
    getQuizData: (state) => state.quizData,
    getQuizIntro: (state) => state.quizIntro,
    getQuizHasEnded: (state) => state.quizHasEnded,
    isInitiated: (state) => {
      return (
        state.messageData.isInitial !== null ||
        state.messageData.isInitial !== ""
      );
    },
    isApproved: (state) => state.percentage > 60,
  },
  actions: {
    returnQuestionsByQuiz() {
      useAdaptLoaderStore?.start(ELoader.QUIZ);
      const classroomId = localStorage.getItem("classId") ?? undefined;

      const questions$ = new QuizRest().returnQuestionsByQuiz(
        useLearningObjectiveStore?.learningObjective.id,
        { classroomId }
      );

      lastValueFrom(questions$)
        .then((response) => {
          this.quizData = {
            ...response,
            questionList: [
              ...response.questionList,
              ...response.absQuestionsList,
            ],
          };

          this.setAsOpenned();
        })
        .catch(() => {
          this.closeQuiz();
        })
        .finally(() => {
          useAdaptLoaderStore?.stop(ELoader.QUIZ);
        });
    },
    async getQuizByLearningObject() {
      const classroomId = localStorage.getItem("classId") ?? undefined;
      const quiz$ = new QuizRest().getQuizByLearningObject(
        useLearningObjectiveStore?.learningObjective.id,
        {
          classroomId,
        }
      );
      const response = await lastValueFrom(quiz$);
      this.quizIntro = response.quizTO;
      this.messageData = {
        name: response.quizTO.description,
        duration: response.quizTO.duration,
        amountQuestions: response.quizTO.numberQuestions,
        isInitial: useAdaptCourseStore?.course.pastObjectiveId,
      };
      this.quizHasEnded = response.done;
      response.done
        ? useFeedbackStore?.set(EFeedback.OBJECTIVE)
        : useAlertStore?.setCurrentAlert(TypeAlert.QUIZ, true);
    },
    proceedQuiz(nextStep: ProceedQuizType) {
      if (nextStep in strategyMap) strategyMap[nextStep]();
      this.resetQuiz();
    },
    quizEnded(state: { percentage: number; value: boolean }): void {
      this.quizHasEnded = state.value;
      this.percentage = state.percentage;
      CourseStore.quiz.pipe(take(1)).subscribe({
        next: () => {},
      });
    },
    closeQuiz() {
      useAlertStore?.setCurrentAlert(TypeAlert.QUIZ, true);
      useAdaptProgressStore.status = StatusProgress.QUIZ;
      useAdaptProgressStore?.setNextState();
    },
    setAsOpenned() {
      this.isOpened = true;
    },
    resetQuiz() {
      (this.quizData = new QuizData()),
        (this.quizIntro = new StudentQuiz()),
        (this.isOpened = false),
        (this.messageData = {});
    },
  },
});
export const useAdaptQuizStore = store(instance);
